
    // IMPORTS
    import { defineComponent, computed, PropType } from 'vue'
    import { Input, TextArea } from "@progress/kendo-vue-inputs"
    import { Error } from "@progress/kendo-vue-labels"
    import { buildLocalization, StringUtil, useFieldValidation } from '@/util'

    export default defineComponent({
        name: 'inputField',

        components: {
            error: Error,
            'k-input': Input,
            'k-text': TextArea
        },

        props: {
            id: { type: String as PropType<string>, required: true },
            modelValue: { type: String as PropType<string>, required: true },
            label: { type: String as PropType<string>, required: true },
            placeholder: { type: String as PropType<string>, required: false, default: () => '' },
            required: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            disabled: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            validator: { type: Function as PropType<(value: string) => string | null>, required: false, default: () => null },
            height: { type: Number as PropType<number>, required: false, default: () => null },
            showValidState: { type: Boolean as PropType<boolean>, required: false, default: () => false },
        },

        emits: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            'update:modelValue': (value: string) => true,
        },

        setup(props, { emit }) {
            /********************************************************************************************************************
             STATE
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            const { showError, errorMessage, errorId, clearError } = useFieldValidation(props.id, () => {
                if (props.disabled) return null
                if (props.required && StringUtil.isNullOrEmpty(props.modelValue)) return localize('requiredEntry')
                if (props.validator) return props.validator(props.modelValue)
                return null
            })

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const computedLabel = computed(() => (props.required || props.disabled)
                ? props.label
                : `${props.label} (${localize('optional')})`
            )
            const computedPlaceholder = computed(() => (props.required || props.disabled)
                ? props.placeholder
                : `${props.placeholder} (${localize('optional')})`
            )
            const heightStyle = computed(() => ({ height: `${props.height}px` }))

            const computedData = { computedLabel, computedPlaceholder, showError, errorMessage, errorId, heightStyle }

            /********************************************************************************************************************
             EVENT HANDLERS
             ********************************************************************************************************************/
            function onChange(e: { value: string }) {
                clearError()
                emit('update:modelValue', e.value)
            }

            const publicMethods = { onChange }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...computedData, ...publicMethods }
        }
    });
